import React from "react";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const humanGeographyUnit7Notes: UnitNotes = {
  unitId: "unit-7",
  unitName: "Unit 7: Industrial and Economic Development Patterns and Processes",
  examWeight: "12–17%",
  focus: "Understanding economic systems, development measures, industrialization, and global trade patterns.",
  sections: [
    {
      id: "topic-7-1",
      title: "Topic 7.1: Economic Systems and Sectors",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Economic Sectors</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Economies are divided into sectors based on the type of economic activity.
            </p>

            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🌾 Primary Sector</h4>
                <p className="text-sm text-slate-700 mt-1">Extraction of raw materials from the Earth.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Agriculture, mining, fishing, forestry, oil drilling</p>
                <p className="text-xs text-slate-600"><strong>Dominant in:</strong> LDCs</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🏭 Secondary Sector</h4>
                <p className="text-sm text-slate-700 mt-1">Processing raw materials into finished goods (manufacturing).</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Factories, construction, food processing</p>
                <p className="text-xs text-slate-600"><strong>Dominant in:</strong> Newly Industrializing Countries (NICs)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">💼 Tertiary Sector</h4>
                <p className="text-sm text-slate-700 mt-1">Services provided to consumers and businesses.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Retail, healthcare, education, entertainment, tourism</p>
                <p className="text-xs text-slate-600"><strong>Dominant in:</strong> MDCs</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🧠 Quaternary Sector</h4>
                <p className="text-sm text-slate-700 mt-1">Information and knowledge-based services.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Research, finance, IT, consulting, government</p>
                <p className="text-xs text-slate-600"><strong>Dominant in:</strong> Post-industrial MDCs</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Economic Transition</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">As countries develop, their economies shift from primary → secondary → tertiary → quaternary sectors.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Example:</strong> UK: Agricultural → Industrial → Service-based → Knowledge-based</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-2",
      title: "Topic 7.2: Measuring Development",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Gross Domestic Product (GDP)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">The total monetary value of all goods and services produced within a country in a year.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Limitation:</strong> Doesn't account for quality of life, inequality, or environmental damage.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">GDP Per Capita</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700">GDP divided by population. Indicates average wealth per person.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Limitation:</strong> Doesn't show distribution of wealth (inequality).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Human Development Index (HDI)</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700">Combines GDP per capita, life expectancy, and education level.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Advantage:</strong> More holistic measure of development than GDP alone.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Other Development Indicators</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <KeyTerm term="Life Expectancy" definition="Average number of years a person is expected to live. Higher in MDCs." />
              <KeyTerm term="Infant Mortality Rate (IMR)" definition="Deaths of infants under 1 year per 1,000 live births. Lower in MDCs." />
              <KeyTerm term="Literacy Rate" definition="Percentage of population that can read and write. Higher in MDCs." />
              <KeyTerm term="Access to Clean Water" definition="Percentage with access to safe drinking water. Higher in MDCs." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-3",
      title: "Topic 7.3: The Industrial Revolution",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Context and Timing</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Began in Britain around 1760 and spread to Europe and North America by the 1800s.</p>
              <p className="text-sm text-slate-700 mt-2">Marked the shift from agrarian, handcraft economies to machine-based, factory production.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Innovations</h3>
            <div className="space-y-3">
              <KeyTerm term="Steam Engine" definition="Powered factories and transportation (trains, ships)." />
              <KeyTerm term="Textile Machinery" definition="Spinning jenny, water frame, power loom increased production." />
              <KeyTerm term="Iron and Steel" definition="Improved production methods allowed mass production." />
              <KeyTerm term="Coal" definition="Primary energy source for factories and transportation." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Impacts</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">✅ Positive</h4>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li>Increased production and wealth</li>
                  <li>Technological innovation</li>
                  <li>Urbanization and growth</li>
                </ul>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">❌ Negative</h4>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li>Harsh working conditions</li>
                  <li>Child labor</li>
                  <li>Pollution and environmental damage</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-4",
      title: "Topic 7.4: Industrialization Patterns",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Core-Periphery Model</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Developed by Immanuel Wallerstein. Describes the global economy as divided into:</p>
              <ul className="text-sm text-slate-700 space-y-2 mt-2">
                <li><strong>Core:</strong> Wealthy, industrialized countries (MDCs) that control the global economy.</li>
                <li><strong>Periphery:</strong> Poor, non-industrialized countries (LDCs) that provide raw materials and cheap labor.</li>
                <li><strong>Semi-Periphery:</strong> Intermediate countries (NICs) that are industrializing.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Dependency Theory</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">Periphery countries are economically dependent on core countries, creating a cycle of poverty.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Mechanism:</strong> Periphery exports raw materials at low prices, imports manufactured goods at high prices.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Newly Industrializing Countries (NICs)</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700">Countries that have recently begun rapid industrialization and economic growth.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Examples:</strong> South Korea, Taiwan, Singapore, Brazil, Mexico, China, India</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Strategy:</strong> Export-oriented manufacturing, foreign investment, cheap labor.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-5",
      title: "Topic 7.5: Factors Influencing Industrial Location",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Classical Location Theory (Weber)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Alfred Weber argued that industries locate where production costs are minimized.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Key Factors:</strong> Transportation costs, labor costs, agglomeration.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Factors Affecting Location</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🚚 Transportation</h4>
                <p className="text-sm text-slate-700 mt-1">Access to ports, railroads, highways. Heavy industries near raw materials or markets.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">👷 Labor</h4>
                <p className="text-sm text-slate-700 mt-1">Availability of skilled/unskilled workers. Wages and labor laws matter.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">⚡ Energy</h4>
                <p className="text-sm text-slate-700 mt-1">Access to electricity, coal, oil. Energy-intensive industries need cheap power.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🏭 Agglomeration</h4>
                <p className="text-sm text-slate-700 mt-1">Clustering of industries in the same area (economies of scale, shared suppliers).</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">📋 Government Policy</h4>
                <p className="text-sm text-slate-700 mt-1">Subsidies, tax breaks, trade agreements, environmental regulations.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-6",
      title: "Topic 7.6: Deindustrialization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">The decline of manufacturing industries in a region or country, often accompanied by job losses and economic hardship.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Causes</h3>
            <div className="space-y-3">
              <KeyTerm term="Outsourcing" definition="Companies move manufacturing to countries with cheaper labor (e.g., US factories to Mexico/China)." />
              <KeyTerm term="Automation" definition="Robots and computers replace factory workers." />
              <KeyTerm term="Shift to Services" definition="Developed economies shift to service/knowledge sectors." />
              <KeyTerm term="Global Competition" definition="Factories in LDCs can produce goods cheaper." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Impacts</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <ul className="text-sm text-slate-700 space-y-2">
                <li>🏭 Factory closures and job losses</li>
                <li>📉 Economic decline in industrial regions (Rust Belt)</li>
                <li>🏚️ Urban decay and abandoned buildings</li>
                <li>💔 Social problems (poverty, crime, substance abuse)</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Examples</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700"><strong>Rust Belt (USA):</strong> Decline of steel and auto manufacturing in the Midwest (Detroit, Pittsburgh, Cleveland)</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Northern England:</strong> Decline of coal mining and textile manufacturing</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-7",
      title: "Topic 7.7: Globalization and Trade",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Globalization</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">The increasing interconnection of the world's economies, cultures, and societies through trade, investment, and technology.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Trade Agreements</h3>
            <div className="space-y-3">
              <KeyTerm term="NAFTA/USMCA" definition="Trade agreement between USA, Canada, Mexico. Reduced tariffs on goods." />
              <KeyTerm term="EU (European Union)" definition="Economic and political union of 27 European countries. Free movement of goods, services, capital, people." />
              <KeyTerm term="WTO (World Trade Organization)" definition="International organization that regulates trade between countries." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Transnational Corporations (TNCs)</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700">Large companies that operate in multiple countries, often with headquarters in core countries and operations in periphery countries.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Examples:</strong> Apple, Nike, Coca-Cola, McDonald's</p>
              <div className="grid md:grid-cols-2 gap-2 mt-2">
                <p className="text-xs text-slate-600"><strong>✅ Pros:</strong> Jobs, technology transfer, economic growth</p>
                <p className="text-xs text-slate-600"><strong>❌ Cons:</strong> Exploitation, environmental damage, cultural homogenization</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Global Supply Chains</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700">Products are made in multiple countries. Raw materials sourced from one country, manufacturing in another, assembly in a third, sold globally.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Example:</strong> iPhone designed in USA, components made in multiple countries, assembled in China, sold worldwide.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-8",
      title: "Topic 7.8: Sustainable Development",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700">Development that meets the needs of the present without compromising the ability of future generations to meet their own needs.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Three Pillars</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🌍 Environmental</h4>
                <p className="text-sm text-slate-700 mt-1">Protecting ecosystems and natural resources.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">👥 Social</h4>
                <p className="text-sm text-slate-700 mt-1">Ensuring equity, justice, and quality of life.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">💰 Economic</h4>
                <p className="text-sm text-slate-700 mt-1">Maintaining economic growth and prosperity.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sustainable Development Goals (SDGs)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">17 goals adopted by the UN in 2015 to end poverty, protect the planet, and ensure peace and prosperity by 2030.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Examples:</strong> No poverty, zero hunger, good health, quality education, clean energy, decent work, climate action.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Challenges</h3>
            <div className="space-y-3">
              <KeyTerm term="Tragedy of the Commons" definition="When individuals act in self-interest, depleting shared resources (overfishing, deforestation)." />
              <KeyTerm term="Externalities" definition="Costs of production not reflected in market prices (pollution, carbon emissions)." />
              <KeyTerm term="Short-term vs. Long-term" definition="Governments and businesses prioritize short-term profits over long-term sustainability." />
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default humanGeographyUnit7Notes;